import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ItemSummaryReportService from 'services/ItemSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import Info from 'components/utils/Alert/Info';
import setDelay from 'helpers/LoadingDelay';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import AlertHelper from 'helpers/AlertHelper';
import Error from 'components/utils/Alert/Error';
import { ShopContext } from 'Context/ShopContext';
import { SelectFilterStyle, PRINT_TYPES, ITEM_SORTING_OPTIONS } from 'const';
import PrintDocument from 'components/utils/PrintDocument/index';
import { AlertMessagesContext } from 'react-alert-messages';
import CsvHelper from 'helpers/CsvHelper';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { InventoryContext } from 'Context/InventoryContext';
import { useTranslation } from 'react-i18next';
import { useReportPrint } from 'hooks/useReportPrint';

function ItemSummaryReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { categories, getCategories } = useContext(InventoryContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterTax, setFilterTax] = useState('all');
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [taxes, setTaxes] = useState('');
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [errorMsg, setErrorMsg] = useState();
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [sorting, setSorting] = useState('QUANTITY_DESC');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterCategory,
    filterTax,
    sorting,
  });
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    id: tCommon('PLU'),
    name: {
      title: tCommon('Item Name'),
      getValue: (datum) => `${datum?.name || ''}${datum?.option_label ? ` - ${datum.option_label}` : ''}`,
    },
    hsn: tCommon('HSN'),
    price: {
      title: tCommon('Unit Price'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.price || 0);
      },
    },
    tax: tCommon('Tax(%)'),
    tax_amount: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.tax_amount || 0);
      },
    },
    cess: tCommon('Additional Charges(%)'),
    cess_amount: {
      title: tCommon('Additional Charges'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.cess_amount || 0);
      },
    },
    purchase_rate: {
      title: tCommon('Purchase Cost'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_rate || 0);
      },
    },
    purchase_tax: {
      title: tCommon('Purchase Tax'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_tax || 0);
      },
    },
    purchase_expense: {
      title: tCommon('Expense'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.purchase_expense || 0);
      },
    },
    damage_value: tCommon('Damage'),
    profit: {
      title: tCommon('Profit'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.profit || 0);
      },
    },
    sold_quantity: tCommon('Sold Qty'),
    unit: tCommon('Unit'),
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const arraysAreEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val) => b.includes(val));
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      !DateTimeHelpers.isSameDate(filterFromDate, prevFilterData.filterFromDate) ||
      !DateTimeHelpers.isSameDate(filterToDate, prevFilterData.filterToDate) ||
      filterCategory !== prevFilterData.filterCategory ||
      filterTax !== prevFilterData.filterTax ||
      !arraysAreEqual(filterTerminal, prevFilterData.filterTerminal) ||
      !arraysAreEqual(filterSalesperson, prevFilterData.filterSalesperson) ||
      sorting !== prevFilterData.sorting
    );
  }, [
    filterFromDate,
    filterToDate,
    filterCategory,
    filterTax,
    filterTerminal,
    filterSalesperson,
    sorting,
    prevFilterData,
  ]);

  useEffect(() => {
    if (!ShopsHelper.hasProAccess(shop)) return;
    generateItemSummaryReports();
    getSalespersons();
    getVat();
    getTerminals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Item Summary Report With Profit'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Total Expense')]: summary.totalExpence,
        [tCommon('Total Profit')]: summary.totalProfit,
        [tCommon('Total Amount')]: summary.totalAmount,
        [tCommon('Total Tax')]: summary.totalTax,
        [tCommon('Total Additional Tax')]: summary.totalAdditionalTax,
        [tCommon('Total Damage')]: summary.totalDamage,
      }
    );
  };

  const generateItemSummaryReports = () => {
    const { startDate, endDate } = dynamicDateRange('PROFIT_ITEM_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadItemSummaryReports({
      startTime: startDate,
      endTime: endDate,
      category: filterCategory,
      tax: filterTax,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
      sorting,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterCategory,
      filterSalesperson,
      filterTerminal,
      filterTax,
      sorting,
    });
  };

  const loadItemSummaryReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    category = filterCategory,
    tax = filterTax,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
    item_sorting = sorting,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = DateTimeHelpers.getTimezoneOffsetDate(startTime);
      const timezoneFreeEndTime = DateTimeHelpers.getTimezoneOffsetDate(endTime);

      const data = {
        filter_start_time: timezoneFreeStartTime,
        filter_end_time: timezoneFreeEndTime,
        category_id: category === 'all' ? undefined : category,
        filter_tax: tax === 'all' ? undefined : [tax],
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
        sorting: item_sorting,
      };

      const resp = await ItemSummaryReportService.getItemSummaryReportWithProfit(data);
      setItems(resp);
      setDelay(setLoadingIndicator);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    } finally {
      setLoadingIndicator(false);
    }
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const getVat = async () => {
    const res = await ItemSummaryReportService.getVat();
    setTaxes(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Item Summary Report With Profit for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getItemsReportSummary(filteredItems);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'name':
          return tCommon('Total');
        case 'tax_amount':
          return summary.totalTax;
        case 'cess_amount':
          return summary.totalAdditionalTax;
        case 'purchase_expense':
          return summary.totalExpence;
        case 'damage_value':
          return summary.totalDamage;
        case 'profit':
          return summary.totalProfit;
        case 'total':
          return summary.totalAmount;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PROFIT-ITEM-SUMMARY-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getItemsFiltered = (items, searchText) => {
    if (!searchText) {
      return items;
    }
    const query = searchText.toLowerCase();

    return items.filter(
      (item) =>
        item.plu?.toString().includes(query) ||
        item.name?.toLowerCase().includes(query) ||
        item.option_label?.toLowerCase().includes(query) ||
        `${item.name} ${item.option_label || ''}`.toLowerCase().includes(query)
    );
  };

  const _getItemsReportSummary = (items) => {
    let totalExpence = 0;
    let totalAmount = 0;
    let totalProfit = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    let totalDamage = 0;
    const salesItems = items.filter(
      (item) => item.name?.toLowerCase() !== 'discount' && String(item.plu) !== '9999996'
    );

    salesItems.forEach((item) => {
      totalExpence += item.purchase_expense;
      totalAmount += item.total;
      totalProfit += item.profit;
      totalTax += item.tax_amount;
      totalAdditionalTax += item.cess_amount;
      totalDamage += item.damage_value;
    });

    return {
      totalExpence: ShopsHelper.getAmountFormatted(shop, totalExpence),
      totalProfit: ShopsHelper.getAmountFormatted(shop, totalProfit),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
      totalDamage: ShopsHelper.getAmountFormatted(shop, totalDamage),
      rawProfit: totalProfit,
    };
  };

  const headerData = [
    {
      label: tCommon('PLU'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'callback',
      viewRender: (item) => `${item.name} ${item.option_label ? `- ${item.option_label}` : ''}`,
    },
    {
      label: tCommon('Unit Price'),
      id: 'price',
      type: 'text',
    },
    {
      label: tCommon('Tax(%)'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax(%)'),
      id: 'cess',
      type: 'text',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Sold Quantity'),
      id: 'sold_quantity',
      type: 'callback',
      viewRender: (item) => {
        if (item.sold_quantity === '' || isNaN(item.sold_quantity)) {
          return item.sold_quantity;
        }
        return `${ShopsHelper.getAmountFormatted(shop, item.sold_quantity)} ${item.unit || ''}`;
      },
    },
    {
      label: tCommon('Purchase Rate'),
      id: 'purchase_rate',
      type: 'callback',
      viewRender: (item) => {
        if (item.purchase_rate === '' || isNaN(item.purchase_rate)) {
          return item.purchase_rate;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.purchase_rate)}`;
      },
    },
    {
      label: tCommon('Purchase Tax'),
      id: 'purchase_tax',
      type: 'callback',
      viewRender: (item) => {
        if (item.purchase_tax === '' || isNaN(item.purchase_tax)) {
          return item.purchase_tax;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.purchase_tax)}`;
      },
    },
    {
      label: tCommon('Expense'),
      id: 'purchase_expense',
      type: 'callback',
      viewRender: (item) => {
        if (item.purchase_expense === '' || isNaN(item.purchase_expense)) {
          return item.purchase_expense;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(
          shop,
          item.purchase_expense,
          shop?.tax_region
        )}`;
      },
    },
    {
      label: tCommon('Damage Value'),
      id: 'damage_value',
      type: 'callback',
      viewRender: (item) => {
        if (item.damage_value === '' || isNaN(item.damage_value)) {
          return item.damage_value;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.damage_value)}`;
      },
    },
    {
      label: tCommon('Profit*'),
      id: 'profit',
      type: 'callback',
      viewRender: (item) => {
        if (item.profit === '' || isNaN(item.profit)) {
          return item.profit;
        }
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.profit)}`;
      },
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${item.total}`;
      },
    },
  ];

  const filteredItems = _getItemsFiltered(items, searchText);
  const summary = _getItemsReportSummary(filteredItems);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {' '}
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        {ShopsHelper.hasProAccess(shop) && (
          <>
            <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Print Report')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={handlePrintReport}
              >
                <PrintIcon className={styles.actionBtnIcon} />
                {tCommon('Print')}
              </Button>
            </div>
            <div style={{ justifyContent: 'flex-end' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Export As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={toCsvBtnPressed}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('CSV')}
              </Button>
            </div>
          </>
        )}
        {!ShopsHelper.hasProAccess(shop) && (
          <div style={{ justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              style={{ backgroundColor: '#1d6df2', color: '#fff', borderRadius: '50px', textTransform: 'none', fontWeight: 600 }}
              onClick={() => window.dispatchEvent(new CustomEvent('open-plan-price'))}
            >
              Upgrade Plan
            </Button>
          </div>
        )}
      </div>
      {!ShopsHelper.hasProAccess(shop) && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tReport("'To get item summary report with profit option upgrade subscription to premium'")}
        />
      )}
      {ShopsHelper.hasProAccess(shop) && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tReport('Item Summary Report With Profit for {{from}} to {{to}}', {
                    from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                    to: DateTimeHelpers.convertDateToDMY(filterToDate),
                  })}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tCommon('Search by plu, name')}
                  />
                  <Tooltip title="Search" placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                  storageKey="PROFIT_ITEM_SUMMARY"
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Category')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterCategory) => {
                        if (filterCategory.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterCategory.map(
                          (id) => categories.find((category) => category.id === id).name
                        );
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {(categories || []).map((category) => (
                        <MenuItem key={category.id} value={category.id}>
                          <Checkbox checked={filterCategory.includes(category.id)} color="primary" />
                          <ListItemText primary={category?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Terminal')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterTerminal}
                      onChange={(e) => setFilterTerminal(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterTerminal) => {
                        if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {terminals.map((terminal) => (
                        <MenuItem key={terminal.id} value={terminal.id}>
                          <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                          <ListItemText primary={terminal.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Salesperson')}</label>
                </div>
                <div>
                  <FormControl>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      displayEmpty
                      value={filterSalesperson}
                      onChange={(e) => setFilterSalesperson(e.target.value)}
                      input={<OutlinedInput />}
                      style={{ width: 135, height: 40 }}
                      renderValue={(filterSalesperson) => {
                        if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                        const selectedNames = filterSalesperson.map(
                          (id) => salesPersons.find((person) => person.id === id).name
                        );
                        return selectedNames.join(', ');
                      }}
                      MenuProps={SelectFilterStyle}
                    >
                      {salesPersons.map((salesperson) => (
                        <MenuItem key={salesperson.id} value={salesperson.id}>
                          <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                          <ListItemText primary={salesperson?.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Tax')}</label>
                </div>
                <div>
                  <TextField
                    select
                    name="category"
                    size="small"
                    variant="outlined"
                    defaultValue={filterTax}
                    color="primary"
                    className={styles.textfeild}
                    onChange={(event) => {
                      setFilterTax(event.target.value);
                    }}
                  >
                    {taxes &&
                      taxes.map((vat) => (
                        <MenuItem key={vat} value={vat}>
                          {vat}
                        </MenuItem>
                      ))}
                    <MenuItem value="all">{tCommon('All')}</MenuItem>
                  </TextField>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Sort by')}</label>
                </div>
                <div>
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    defaultValue={sorting}
                    color="primary"
                    className={styles.textfeild}
                    onChange={(event) => {
                      setSorting(event.target.value);
                    }}
                  >
                    {Object.values(ITEM_SORTING_OPTIONS)?.map((op) => (
                      <MenuItem key={op.value} value={op.value}>
                        {tCommon(op.labelTxKey)}
                      </MenuItem>
                    ))}
                  </TextField>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tCommon('Generate Report')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generateItemSummaryReports}
                    disabled={isShowGenerateButton === false || loadingIndicator}
                  >
                    {loadingIndicator ? (
                      <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                    ) : (
                      <SyncIcon className={styles.actionBtnIcon} />
                    )}
                    {tCommon('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}

          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : (
            items &&
            (filteredItems && filteredItems.length ? (
              <>
                <div className={styles.summaryContainer}>
                  <div className={`${styles.summaryCard} ${styles.cardBlue}`}>
                    <span className={styles.cardTitle}>{tCommon('Total Amount')}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalAmount}`}</div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Tax Amount')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</strong>
                      </span>
                    </div>
                  </div>

                  <div
                    className={`${styles.summaryCard} ${summary.rawProfit >= 0 ? styles.cardGreen : styles.cardRed}`}
                  >
                    <span className={styles.cardTitle}>{tCommon('Profit')}</span>
                    <div className={styles.cardValue} style={{ color: summary.rawProfit >= 0 ? '#2e7d32' : '#c62828' }}>
                      {`${shop?.currency || ''} ${summary.totalProfit}`}
                    </div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Additional Tax')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${
                          summary.totalAdditionalTax
                        }`}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardOrange}`}>
                    <span className={styles.cardTitle}>{tCommon('Purchase Cost')}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalExpence}`}</div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Damage')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${
                          summary.totalDamage
                        }`}</strong>
                      </span>
                    </div>
                  </div>

                  <div className={`${styles.summaryCard} ${styles.cardPurple}`}>
                    <span className={styles.cardTitle}>{tCommon('Expense')}</span>
                    <div className={styles.cardValue}>{`${shop?.currency || ''} ${summary.totalDamage}`}</div>
                    <div className={styles.cardDetails}>
                      <span>
                        {tCommon('Tax Amount')}:{' '}
                        <strong className={styles.detailsValue}>{`${shop?.currency || ''} ${summary.totalTax}`}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.tableContainer}>
                  <Grayout open={isShowGenerateButton} />
                  <DataTable
                    columns={headerData}
                    rows={filteredItems}
                    rowKey="id"
                    getRowKey={(row, index) => `${row.item_id || row.plu || row.id}_${row.option_label || ''}_${index}`}
                    summary={{
                      name: tCommon('Total'),
                      purchase_expense: `${shop?.currency} ${summary.totalExpence}`,
                      profit: `${shop?.currency} ${summary.totalProfit}`,
                      total: `${shop?.currency} ${summary.totalAmount}`,
                      tax_amount: `${shop?.currency || ''} ${summary.totalTax}`,
                      cess_amount: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
                      damage_value: `${shop?.currency || ''} ${summary.totalDamage}`,
                    }}
                    pagination={true}
                  />
                </div>
              </>
            ) : (
              !loadingIndicator && (
                <Info
                  severity="warning"
                  title={tReport('Oh no, there are no sales matching to your filter')}
                  content={tReport(
                    'There are no sales matching to your filter. Please verify your inputs to generate report.'
                  )}
                />
              )
            ))
          )}
        </>
      )}
      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(ItemSummaryReport);
